import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  ModalBody,
  ModalFooter
} from 'reactstrap';

import ReactButton from 'components/Buttons/ReactButton';
import FlexSelect from 'components/Inputs/FlexSelect';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { sheetReader } from 'utils/fuctions/sheetReader.js';
import { generateSheetHeaders } from 'utils/fuctions/generateSheetHeaders.js';

import LeadService from '../../../services/leadService';

import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from '../../../../../../hooks/useMemorizeInputsFilters';

const initialState = {
  file: '',
  data: [],
  hasHeader: true,
  columns: []
};

const defaultRequiredFields = [
  {
    id: 'name',
    name: 'name',
    label: 'Nome',
    isRequired: true
  }
];
const defaultOptionalFields = [
  {
    id: 'email',
    name: 'email',
    label: 'Email',
    isRequired: false
  },
  {
    id: 'phone',
    name: 'phone',
    label: 'Telefone',
    isRequired: false
  },
  {
    id: 'responsibleLogin',
    name: 'responsibleLogin',
    label: 'Responsável',
    isRequired: false
  }
];

const ImportLeadsModal = ({ isOpen, setIsOpen, recordTypeId }) => {
  const [state, setState] = useState(initialState);
  const [fields, setFields] = useState(defaultRequiredFields);
  const [optionalFields, setOptionalFields] = useState([]);
  const [option, setOption] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit, control, setValue } = useForm();
  const systemConfiguration = useSelector((stt) => stt.systemConfiguration);

  const { memorizeFilters, getMemorizedFilters } = useMemorizeFilters(
    POSSIBLE_FILTERS_ENTITIES.CRM_LEAD_IMPORT
  );

  const { getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.LEAD,
    recordTypeId
  );

  useEffect(() => {
    const memorizedFields = getMemorizedFilters();

    memorizedFields &&
      memorizedFields.length &&
      memorizedFields.forEach((field) => {
        if (field.recordTypeId === Number(recordTypeId)) {
          let memorizedFieldsDto = field.fields;
          field.fields.forEach((field) => {
            if (
              field.id !== 'name' &&
              !optionalFields.some((customField) => customField.id === field.id)
            ) {
              memorizedFieldsDto = memorizedFieldsDto.filter(
                (element) => element.id !== field.id
              );
            }
          });

          setFields(memorizedFieldsDto);
        } else {
          return;
        }
      });
  }, [getMemorizedFilters, recordTypeId, optionalFields]);

  const saveImportFields = (fields) => {
    const recordTypeIdNumber = Number(recordTypeId);
    const recordTypeIds = [];

    const memorizedFields = getMemorizedFilters();

    const updatedFields =
      memorizedFields &&
      memorizedFields.length &&
      memorizedFields.map((fieldsSaved) => {
        recordTypeIds.push(fieldsSaved.recordTypeId);
        if (fieldsSaved.recordTypeId === recordTypeIdNumber) {
          fieldsSaved.fields = fields;
        }
        return fieldsSaved;
      });

    if (updatedFields) {
      if (!recordTypeIds.includes(recordTypeIdNumber)) {
        const fieldsDTO = {
          recordTypeId: recordTypeIdNumber,
          fields: [...fields]
        };
        updatedFields.push(fieldsDTO);
      }
      memorizeFilters(updatedFields);
    } else {
      const fieldsDTO = [
        { recordTypeId: recordTypeIdNumber, fields: [...fields] }
      ];

      memorizeFilters(fieldsDTO);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const getCtFields = async () => {
        const custFilds = await getCustomFields();
        const fieldsDTO = custFilds.map((field) => ({
          id: field.id,
          name: field.name,
          label: field.label,
          isRequired: field.recordTypesConfig.isRequired
        }));
        setOptionalFields([...defaultOptionalFields, ...fieldsDTO]);
      };
      getCtFields();
    }
  }, [getCustomFields, isOpen]);

  useEffect(() => {
    const [headers] = state.data;
    if (!headers) return;
    const columns = headers.map((e) => ({ col: e }));
    setState((s) => ({ ...s, columns }));
  }, [state.data]);

  const changeFile = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const result = await sheetReader(file);

    const data = result
      .split(/\r?\n|\r/)
      .map((item) => item.split(/,|\t/))
      .filter((item) => item.length > 1);

    const maxLimit = state.hasHeader ? 50001 : 50000;
    if (data.length > maxLimit) {
      toast.error(`A importação deve ter no máximo 50.000 registros`);
      return;
    }

    setState((s) => ({ ...s, data, file }));
  };

  const changeHasHeader = (e) => {
    const hasHeader = e.target.checked;
    if (!hasHeader) {
      const headers = generateSheetHeaders(state.columns.length);
      setState((s) => ({ ...s, hasHeader, data: [headers, ...s.data] }));
    } else {
      setState((s) => {
        s.data.shift();
        return { ...s, hasHeader, data: [...s.data] };
      });
    }
  };

  const filterMatrixByColumns = (columns, matrix) => {
    const filteredMatrix = [];
    for (let i = 0; i < matrix.length; i++) {
      const row = [];
      for (let j = 0; j < columns.length; j++) {
        row.push(matrix[i][columns[j]]);
      }
      filteredMatrix.push(row);
    }
    return filteredMatrix;
  };

  const onAddField = () => {
    if (optionalFields.length === 0 || option === '') return;

    const [field] = optionalFields.filter((f) => f.name === option);

    setOptionalFields(optionalFields.filter((f) => f.name !== option));
    setFields([...fields, field]);
    setOption('');
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      for (const key in data) {
        if (!data[key]) {
          toast.info(`Preencha todos os campos selecionados`);
          return;
        }
      }
      for (const f of optionalFields) {
        if (
          f.isRequired &&
          (!Object.keys(data).includes(f.name) || !data[f.name])
        ) {
          toast.info(`Preencha todos os campos marcados com "*"`);
          return;
        }
      }

      const columns = Object.values(data).map((h) => state.data[0].indexOf(h));
      const filteredData = filterMatrixByColumns(columns, state.data);
      const dataEntries = Object.entries(data);
      const headers = filteredData.shift().map((h) => {
        for (let i = 0; i < dataEntries.length; i++) {
          const [key, value] = dataEntries[i];

          if (h === value) {
            dataEntries.splice(i, 1);

            return key;
          }
        }
      });

      const dataDTO = [];
      for (const fieldData of filteredData) {
        const item = {};
        for (const header of headers) {
          item[header] = fieldData[headers.indexOf(header)];
        }
        dataDTO.push(item);
      }

      await LeadService.importLeads(recordTypeId, { data: dataDTO });

      toast.success(`${dataDTO.length} leads importados com sucesso`);
      setState(initialState);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao importar leads';
      toast.error(msg);
    } finally {
      saveImportFields(fields);
      setIsSubmitting(false);
    }
  };

  const onRemoveField = (index) => {
    const field = fields[index];
    const newFields = fields.filter((f) => f.id !== field.id);

    setFields(newFields);
    memorizeFilters(newFields);
  };

  return (
    <>
      {!state.file ? (
        <ModalBody>
          <Form>
            <FormGroup>
              <label className="form-control-label d-block mb-3">
                Insira um arquivo do tipo CSV, XLS ou XLSX
              </label>
              <label
                htmlFor="import_leads_list"
                className="btn btn-primary btn-sm"
                style={buttonStyle(systemConfiguration.primary_color)}
              >
                Selecionar um arquivo
              </label>
              <Input
                style={{ display: 'none' }}
                type="file"
                id="import_leads_list"
                name="import_leads_list"
                accept=".csv, .xls, .xlsx"
                value={state.file}
                onChange={changeFile}
              />
            </FormGroup>
          </Form>
        </ModalBody>
      ) : (
        <>
          <ModalBody>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '0.875rem' }}>
                Arquivo
              </div>
              <div
                style={{
                  color: '#525f7f',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                {state.file.name}
              </div>
            </div>
            <Form id="import-leads-form" onSubmit={handleSubmit(onSubmit)}>
              <FormGroup check style={{ marginBottom: '24px' }}>
                <Label check>
                  <Input
                    type="checkbox"
                    name="hasHeader"
                    checked={state.hasHeader}
                    onChange={changeHasHeader}
                  />
                  Utilizar primeira linha como cabeçalho
                </Label>
              </FormGroup>
              {fields.map((field, index) => (
                <Controller
                  key={field.id}
                  control={control}
                  name={field.name}
                  defaultValue=""
                  render={(props) => (
                    <div
                      style={{
                        display: 'flex'
                      }}
                    >
                      <div style={{ paddingRight: '8px', width: '100%' }}>
                        <FormGroup>
                          <Label className="form-control-label text-capitalize">
                            {`${field.label}${field.isRequired ? '*' : ''}`}
                          </Label>
                          <FlexSelect
                            isClearable={true}
                            dataOptions={state.columns}
                            isMulti={false}
                            closeMenuOnSelect={true}
                            value={props.value}
                            valueController={setValue}
                            fieldName={field.name}
                            labelName="col"
                            valueName="col"
                          />
                        </FormGroup>
                      </div>

                      <Button
                        style={{
                          alignSelf: 'center',
                          marginTop: '7px',
                          minHeight: '46px'
                        }}
                        type="button"
                        color="danger"
                        disabled={index === 0 ? true : false}
                        onClick={() => {
                          onRemoveField(index);
                        }}
                      >
                        <li className="fas fa-times"></li>
                      </Button>
                    </div>
                  )}
                />
              ))}
              <div>
                <div style={{ marginBottom: '8px', fontSize: '0.875rem' }}>
                  Adicionar coluna
                </div>
                <div style={{ display: 'flex' }}>
                  <div style={{ paddingRight: '8px', width: '100%' }}>
                    <FlexSelect
                      isClearable={true}
                      isDisabled={optionalFields.length === 0 ? true : false}
                      dataOptions={optionalFields.map(
                        ({ label, name, isRequired }) => ({
                          label: `${label}${isRequired ? '*' : ''}`,
                          value: name
                        })
                      )}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      value={option}
                      valueController={(_, value) => {
                        setOption(value);
                      }}
                      fieldName="option"
                      labelName="label"
                      valueName="value"
                    />
                  </div>
                  <Button
                    type="button"
                    color="default"
                    disabled={option === '' ? true : false}
                    onClick={onAddField}
                  >
                    <li className="fas fa-plus"></li>
                  </Button>
                </div>
              </div>
            </Form>
          </ModalBody>
          <ModalFooter>
            <ReactButton
              btnColor="confirmation"
              type="submit"
              form="import-leads-form"
              disabled={!state.file || isSubmitting}
            >
              Importar
            </ReactButton>
            <ReactButton
              btnColor="cancelation"
              onClick={() => {
                setState(initialState);
                setOption('');
                setIsOpen(!isOpen);
              }}
            >
              Cancelar
            </ReactButton>
          </ModalFooter>
        </>
      )}
    </>
  );
};

export default ImportLeadsModal;
